import React, { useEffect, useRef, useState } from "react";

const ASPECT_CLASS = {
  "4x3": "landing-v2-placeholder--4x3",
  "16x9": "landing-v2-placeholder--16x9",
};

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

const getPrefersReducedMotion = () => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }

  return window.matchMedia(REDUCED_MOTION_QUERY).matches;
};

const DeferredLandingVideo = ({
  src,
  poster,
  alt,
  aspectRatio = "16x9",
  className = "",
  videoClassName = "landing-v2-media__video",
  controls = false,
  loop = true,
  rootMargin = "220px 0px",
  threshold = 0.1,
}) => {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const [shouldLoadSource, setShouldLoadSource] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(getPrefersReducedMotion);
  const aspectClass = ASPECT_CLASS[aspectRatio] ?? ASPECT_CLASS["16x9"];

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQuery = window.matchMedia(REDUCED_MOTION_QUERY);
    const handleChange = (event) => setPrefersReducedMotion(event.matches);

    setPrefersReducedMotion(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return undefined;
    }

    if (!("IntersectionObserver" in window)) {
      setShouldLoadSource(true);
      setIsInView(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        const nextIsInView = entry.isIntersecting;
        setIsInView(nextIsInView);

        if (nextIsInView) {
          setShouldLoadSource(true);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, [rootMargin, threshold]);

  useEffect(() => {
    const video = videoRef.current;

    if (!video) {
      return;
    }

    if (!isInView || prefersReducedMotion) {
      video.pause?.();
      return;
    }

    if (shouldLoadSource) {
      video.play?.().catch(() => {
        // Autoplay can still be blocked by browser policy; the poster remains visible.
      });
    }
  }, [isInView, prefersReducedMotion, shouldLoadSource]);

  return (
    <div ref={containerRef} className={`landing-v2-media ${aspectClass} ${className}`.trim()}>
      {shouldLoadSource ? (
        <video
          ref={videoRef}
          className={videoClassName}
          src={src}
          poster={poster}
          controls={controls || prefersReducedMotion}
          loop={loop}
          muted
          playsInline
          preload="none"
          aria-label={alt}
        />
      ) : (
        <img
          src={poster}
          alt={alt}
          className={videoClassName}
          loading="lazy"
          decoding="async"
        />
      )}
    </div>
  );
};

export default DeferredLandingVideo;
